import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { useData } from "../lib/DataContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate } from "../lib/ui";
import { toast } from "sonner";

const PRIO = { Critical: "#DC2626", High: "#EA580C", Medium: "#2563EB", Low: "#94A3B8" };
const DAY = 86400000;
function dayDiff(a, b) { return Math.round((new Date(b) - new Date(a)) / DAY); }
function shiftDate(s, days) { const d = new Date(s); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }

export default function Timeline() {
  const { projects } = useData();
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [drag, setDrag] = useState(null);
  const trackRef = useRef(null);

  const reload = () => { if (pid) api.get(`/projects/${pid}/timeline`).then((r) => setData(r.data)); };
  useEffect(() => { if (projects.length && !pid) setPid(projects[0].id); }, [projects, pid]);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [pid]);

  let minD = null, maxD = null;
  if (data?.tasks?.length) {
    data.tasks.forEach((t) => {
      if (t.start) { const d = new Date(t.start); if (!minD || d < minD) minD = d; }
      const f = t.actual_finish || t.finish;
      if (f) { const d = new Date(f); if (!maxD || d > maxD) maxD = d; }
    });
  }
  if (minD && maxD) { minD = new Date(minD); minD.setDate(minD.getDate() - 2); maxD = new Date(maxD); maxD.setDate(maxD.getDate() + 2); }
  const span = minD && maxD ? Math.max(1, dayDiff(minD, maxD)) : 1;
  const todayLeft = minD ? (dayDiff(minD, new Date()) / span) * 100 : 0;

  const pxPerDay = () => {
    const w = trackRef.current?.offsetWidth || 820;
    return Math.max(2, (w - 256 - 24) / span);
  };

  const startDrag = (t, e) => {
    e.preventDefault(); e.stopPropagation();
    const ppd = pxPerDay(); const x0 = e.clientX;
    setDrag({ id: t.id, deltaDays: 0 });
    const move = (ev) => { const dd = Math.round((ev.clientX - x0) / ppd); setDrag((d) => (d ? { ...d, deltaDays: dd } : d)); };
    const up = async (ev) => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      const dd = Math.round((ev.clientX - x0) / ppd);
      setDrag(null);
      if (dd !== 0 && t.start && t.finish) {
        try {
          await api.post(`/tasks/${t.id}/reschedule`, { start_date: shiftDate(t.start, dd), current_due_date: shiftDate(t.finish, dd), source: "gantt" });
          toast.success(`Rescheduled ${dd > 0 ? "+" : ""}${dd} day(s)`);
          reload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not reschedule"); }
      }
    };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  return (
    <div className="p-6">
      <PageHeader title="Project Timeline" subtitle="Gantt — drag a bar to reschedule (start + due shift together)">
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger className="w-64" data-testid="timeline-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
          <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>
      {!data ? <div className="text-slate-400 text-sm">Loading…</div> : data.tasks.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm bg-white border rounded-xl">No dated tasks in this project yet.</div>
      ) : (
        <div className="bg-white border rounded-xl overflow-x-auto">
          <div className="min-w-[820px]" ref={trackRef}>
            <div className="flex text-xs font-semibold text-slate-500 uppercase border-b bg-slate-50">
              <div className="w-64 shrink-0 px-3 py-2">Task</div>
              <div className="flex-1 px-3 py-2 flex justify-between">
                <span>{minD && fmtDate(minD.toISOString())}</span><span>{maxD && fmtDate(maxD.toISOString())}</span>
              </div>
            </div>
            <div className="relative">
              {todayLeft >= 0 && todayLeft <= 100 && (
                <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: `calc(16rem + ${todayLeft}% * (100% - 16rem)/100)` }} />
              )}
              {data.tasks.map((t) => {
                const s = t.start ? dayDiff(minD, t.start) : 0;
                const e = (t.actual_finish || t.finish) ? dayDiff(minD, t.actual_finish || t.finish) : s + 1;
                const left = (s / span) * 100;
                const width = Math.max(1.5, ((e - s) / span) * 100);
                const plannedE = t.planned_finish ? dayDiff(minD, t.planned_finish) : e;
                const slip = e - plannedE;
                const isDragging = drag?.id === t.id;
                const dx = isDragging ? drag.deltaDays * pxPerDay() : 0;
                const done = t.status === "Completed" || t.status === "Closed";
                return (
                  <div key={t.id} className="flex items-center border-b last:border-0 hover:bg-slate-50" data-testid={`gantt-row-${t.human_id}`}>
                    <button onClick={() => setOpenId(t.id)} className="w-64 shrink-0 px-3 py-2 text-left">
                      <div className="text-xs font-mono text-slate-400">{t.human_id}</div>
                      <div className="text-sm font-medium text-slate-800 truncate">{t.title}</div>
                    </button>
                    <div className="flex-1 relative h-9 px-3">
                      <div className="absolute inset-y-0 left-3 right-3">
                        <div
                          onMouseDown={(ev) => startDrag(t, ev)}
                          data-testid={`gantt-bar-${t.human_id}`}
                          className={`absolute top-1/2 -translate-y-1/2 h-4 rounded cursor-grab active:cursor-grabbing ${isDragging ? "ring-2 ring-blue-400 z-20" : ""}`}
                          style={{ left: `${left}%`, width: `${width}%`, transform: `translate(${dx}px, -50%)`, background: (PRIO[t.priority] || "#2563EB") + (done ? "" : "cc"), opacity: done ? 0.55 : 1 }}
                          title={`${fmtDate(t.start)} → ${fmtDate(t.finish)}  ·  drag to reschedule`} />
                        {slip > 0 && !isDragging && (
                          <div className="absolute top-1/2 -translate-y-1/2 h-4 rounded border border-red-500 border-dashed"
                            style={{ left: `${(plannedE / span) * 100}%`, width: `${Math.max(1, (slip / span) * 100)}%` }} title={`Slipped ${slip}d`} />
                        )}
                        <div className="absolute -bottom-0 text-[9px] text-slate-400" style={{ left: `${left}%` }}>{isDragging && drag.deltaDays !== 0 ? `${drag.deltaDays > 0 ? "+" : ""}${drag.deltaDays}d` : `${t.progress}%`}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {data && data.links.length > 0 && (
        <div className="text-xs text-slate-500 mt-2">{data.links.length} dependency link(s). Dashed red = slippage past planned finish; red line = today.</div>
      )}
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
    </div>
  );
}
